import type {
  SteelOrderCategoryEnum,
  SteelOrderMatchingStatusEnum,
  SteelOrderQualityCheckStatusEnum,
} from '@mbfab/client-api'
import { isPresent, isSome } from '@mbfab/shared'
import { useState } from 'react'
import { z } from 'zod'

import { DateRangeField } from '@/components/ui/date-range-field'
import { FilterBar as FilterBarShell } from '@/components/ui/filter-bar'
import { FilterTextInput } from '@/components/ui/filter-text-input'
import { CompactFormField, CompactFormSelect } from '@/components/ui/form'
import { isRealDateString } from '@/lib/url-filter-values'

// 「対象外」は独立したフィルタではなく、v1 と同じくミルシートステータスの4番目の選択肢として畳み込む
// （isMillsheetExcluded フラグを持つ行を表す。v1 SteelOrderSearchForm.tsx の millsheetStatus と同じ設計。
// v1 には isMillsheetExcluded 専用の絞り込みUIは存在しないため作らない）
export type SteelOrderMillsheetStatusFilterValue = SteelOrderMatchingStatusEnum | 'excluded'

// 発注一覧の検索状態（フリーワード + 詳細条件）。GraphQL の SteelOrdersSearchInput への変換はコンテナ側で行う
export type SteelOrderSearchState = {
  freeWord: string
  referenceNumber: string
  category: SteelOrderCategoryEnum | null
  millsheetStatus: SteelOrderMillsheetStatusFilterValue | null
  orderStatus: SteelOrderMatchingStatusEnum | null
  qualityCheckStatus: SteelOrderQualityCheckStatusEnum | null
  managementNumber: string
  constructionNumber: string
  constructionName: string
  productName: string
  standard: string
  supplierName: string
  manufacturerName: string
  outputDateFrom: string | null
  outputDateTo: string | null
  importedAtFrom: string | null
  importedAtTo: string | null
}

export const EMPTY_STEEL_ORDER_SEARCH_STATE: SteelOrderSearchState = {
  freeWord: '',
  referenceNumber: '',
  category: null,
  millsheetStatus: null,
  orderStatus: null,
  qualityCheckStatus: null,
  managementNumber: '',
  constructionNumber: '',
  constructionName: '',
  productName: '',
  standard: '',
  supplierName: '',
  manufacturerName: '',
  outputDateFrom: null,
  outputDateTo: null,
  importedAtFrom: null,
  importedAtTo: null,
}

const CATEGORY_LABELS: Record<SteelOrderCategoryEnum, string> = { roll: 'ロール品', spot: 'スポット品' }
const MILLSHEET_STATUS_LABELS: Record<SteelOrderMillsheetStatusFilterValue, string> = {
  completed: '完了',
  incomplete: '未完了',
  unregistered: '未登録',
  excluded: '対象外',
}
const MATCHING_STATUS_LABELS: Record<SteelOrderMatchingStatusEnum, string> = {
  completed: '完了',
  incomplete: '未完了',
  unregistered: '未登録',
}
const QUALITY_CHECK_STATUS_LABELS: Record<SteelOrderQualityCheckStatusEnum, string> = {
  completed: '完了',
  incomplete: '未完了',
  unregistered: '未登録',
}

// テキスト絞り込み欄の定義（画面の並び順。v1 SteelOrderSearchForm の TEXT_FILTERS と同じ項目・順序）
const TEXT_FIELDS: Array<{
  key: Extract<
    keyof SteelOrderSearchState,
    | 'referenceNumber'
    | 'managementNumber'
    | 'constructionNumber'
    | 'constructionName'
    | 'productName'
    | 'standard'
    | 'supplierName'
    | 'manufacturerName'
  >
  label: string
}> = [
  { key: 'referenceNumber', label: '参照番号' },
  { key: 'managementNumber', label: '管理番号' },
  { key: 'constructionNumber', label: '工事番号' },
  { key: 'constructionName', label: '工事名称' },
  { key: 'productName', label: '品名' },
  { key: 'standard', label: '規格' },
  { key: 'supplierName', label: 'サプライヤー名' },
  { key: 'manufacturerName', label: '製造会社名' },
]
const PRIMARY_TEXT_FIELDS = TEXT_FIELDS.slice(0, 4)
const MATERIAL_TEXT_FIELDS = TEXT_FIELDS.slice(4)

// select の value（string）を対応する enum へ、型アサーションなしで検証してから絞り込む。
// *_LABELS のキー集合を許容値の正とする（別に許容リストを持つと二重管理になるため）
function isKeyOf<T extends Record<string, unknown>>(record: T, value: string): value is Extract<keyof T, string> {
  return Object.keys(record).includes(value)
}

// URL のクエリから読んだ条件の検証（useFilterBarState の parse）。URL は利用者が書き換えられるため、
// 型の合わないキーはそのキーだけ既定値へ戻し、列挙値は *_LABELS を許容値として未知の値を落とす
const isoDateOrNull = z.string().refine(isRealDateString).nullable().catch(null)
const nullableText = z.string().nullable().catch(null)
const steelOrderSearchSchema = z.object({
  freeWord: z.string().catch(''),
  referenceNumber: z.string().catch(''),
  category: nullableText,
  millsheetStatus: nullableText,
  orderStatus: nullableText,
  qualityCheckStatus: nullableText,
  managementNumber: z.string().catch(''),
  constructionNumber: z.string().catch(''),
  constructionName: z.string().catch(''),
  productName: z.string().catch(''),
  standard: z.string().catch(''),
  supplierName: z.string().catch(''),
  manufacturerName: z.string().catch(''),
  outputDateFrom: isoDateOrNull,
  outputDateTo: isoDateOrNull,
  importedAtFrom: isoDateOrNull,
  importedAtTo: isoDateOrNull,
})
const keyOrNull = <T extends Record<string, unknown>>(
  record: T,
  value: string | null,
): Extract<keyof T, string> | null => (value !== null && isKeyOf(record, value) ? value : null)
export function parseSteelOrderSearchState(raw: unknown): SteelOrderSearchState | undefined {
  const result = steelOrderSearchSchema.safeParse(raw)
  if (!result.success) {
    return undefined
  }
  return {
    ...result.data,
    category: keyOrNull(CATEGORY_LABELS, result.data.category),
    millsheetStatus: keyOrNull(MILLSHEET_STATUS_LABELS, result.data.millsheetStatus),
    orderStatus: keyOrNull(MATCHING_STATUS_LABELS, result.data.orderStatus),
    qualityCheckStatus: keyOrNull(QUALITY_CHECK_STATUS_LABELS, result.data.qualityCheckStatus),
  }
}

type Chip = { key: keyof SteelOrderSearchState; label: string; value: string }

// 適用済み（value）の条件からチップを組み立てる
function buildChips(value: SteelOrderSearchState): Chip[] {
  const chips: Chip[] = []
  // trim せずに isPresent すると、空白だけの値も「値がある」と判定されチップに出てしまう
  // （API 送信側の presentOrNull は trim 後の空文字を null にしているため、表示と食い違う）
  const freeWord = value.freeWord.trim()
  if (isPresent(freeWord)) {
    chips.push({ key: 'freeWord', label: 'フリーワード', value: freeWord })
  }
  for (const field of TEXT_FIELDS) {
    const text = value[field.key].trim()
    if (isPresent(text)) {
      chips.push({ key: field.key, label: field.label, value: text })
    }
  }
  if (isSome(value.category)) {
    chips.push({ key: 'category', label: '分類', value: CATEGORY_LABELS[value.category] })
  }
  if (isSome(value.millsheetStatus)) {
    chips.push({
      key: 'millsheetStatus',
      label: 'ミルシートステータス',
      value: MILLSHEET_STATUS_LABELS[value.millsheetStatus],
    })
  }
  if (isSome(value.orderStatus)) {
    chips.push({ key: 'orderStatus', label: '発注ステータス', value: MATCHING_STATUS_LABELS[value.orderStatus] })
  }
  if (isSome(value.qualityCheckStatus)) {
    chips.push({
      key: 'qualityCheckStatus',
      label: '品管照合ステータス',
      value: QUALITY_CHECK_STATUS_LABELS[value.qualityCheckStatus],
    })
  }
  if (isSome(value.outputDateFrom)) {
    chips.push({ key: 'outputDateFrom', label: '出力日（自）', value: value.outputDateFrom })
  }
  if (isSome(value.outputDateTo)) {
    chips.push({ key: 'outputDateTo', label: '出力日（至）', value: value.outputDateTo })
  }
  if (isSome(value.importedAtFrom)) {
    chips.push({ key: 'importedAtFrom', label: '発注データ更新日（自）', value: value.importedAtFrom })
  }
  if (isSome(value.importedAtTo)) {
    chips.push({ key: 'importedAtTo', label: '発注データ更新日（至）', value: value.importedAtTo })
  }
  return chips
}

export function FilterBar({
  value,
  onChange,
  onPendingChange,
}: {
  value: SteelOrderSearchState
  onChange: (next: SteelOrderSearchState) => void
  onPendingChange?: (pending: boolean) => void
}) {
  const [expanded, setExpanded] = useState(false)

  const patch = (partial: Partial<SteelOrderSearchState>) => {
    onChange({ ...value, ...partial })
  }

  const removeChip = (key: keyof SteelOrderSearchState) => {
    patch({ [key]: EMPTY_STEEL_ORDER_SEARCH_STATE[key] })
  }

  const chips = buildChips(value)
  const detailChipCount = chips.filter((chip) => chip.key !== 'freeWord').length

  return (
    <FilterBarShell
      freeWord={value.freeWord}
      onFreeWordChange={(freeWord) => {
        patch({ freeWord })
      }}
      onPendingChange={onPendingChange}
      freeWordPlaceholder="フリーワードで検索（参照番号・品名・工事名など）"
      expanded={expanded}
      onExpandedChange={setExpanded}
      chips={chips}
      detailFilterCount={detailChipCount}
      onRemoveChip={removeChip}
      onClearAll={() => {
        onChange(EMPTY_STEEL_ORDER_SEARCH_STATE)
      }}
      testIdPrefix="steel-order-filter"
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        {PRIMARY_TEXT_FIELDS.map((field) => (
          <CompactFormField key={field.key} id={field.key} label={field.label}>
            <FilterTextInput
              id={field.key}
              type="text"
              value={value[field.key]}
              onValueChange={(next) => {
                patch({ [field.key]: next })
              }}
              placeholder={`${field.label}を入力`}
              data-testid={`steel-order-filter-${field.key}`}
              clearButtonTestId={`steel-order-filter-${field.key}-clear`}
            />
          </CompactFormField>
        ))}
        <CompactFormField id="category" label="分類">
          <CompactFormSelect
            id="category"
            value={value.category ?? ''}
            onChange={(e) => {
              const value = e.target.value
              patch({ category: isKeyOf(CATEGORY_LABELS, value) ? value : null })
            }}
          >
            <option value="">すべて</option>
            <option value="roll">ロール品</option>
            <option value="spot">スポット品</option>
          </CompactFormSelect>
        </CompactFormField>
        <CompactFormField id="millsheetStatus" label="ミルシートステータス">
          <CompactFormSelect
            id="millsheetStatus"
            value={value.millsheetStatus ?? ''}
            onChange={(e) => {
              const value = e.target.value
              patch({ millsheetStatus: isKeyOf(MILLSHEET_STATUS_LABELS, value) ? value : null })
            }}
          >
            <option value="">すべて</option>
            <option value="completed">完了</option>
            <option value="incomplete">未完了</option>
            <option value="unregistered">未登録</option>
            <option value="excluded">対象外</option>
          </CompactFormSelect>
        </CompactFormField>
        <CompactFormField id="orderStatus" label="発注ステータス">
          <CompactFormSelect
            id="orderStatus"
            value={value.orderStatus ?? ''}
            onChange={(e) => {
              const value = e.target.value
              patch({ orderStatus: isKeyOf(MATCHING_STATUS_LABELS, value) ? value : null })
            }}
          >
            <option value="">すべて</option>
            <option value="completed">完了</option>
            <option value="incomplete">未完了</option>
            <option value="unregistered">未登録</option>
          </CompactFormSelect>
        </CompactFormField>
        <CompactFormField id="qualityCheckStatus" label="品管照合ステータス">
          <CompactFormSelect
            id="qualityCheckStatus"
            value={value.qualityCheckStatus ?? ''}
            onChange={(e) => {
              const value = e.target.value
              patch({ qualityCheckStatus: isKeyOf(QUALITY_CHECK_STATUS_LABELS, value) ? value : null })
            }}
          >
            <option value="">すべて</option>
            <option value="completed">完了</option>
            <option value="incomplete">未完了</option>
            <option value="unregistered">未登録</option>
          </CompactFormSelect>
        </CompactFormField>

        <DateRangeField
          idPrefix="outputDate"
          testIdPrefix="steel-order-filter"
          label="出力日"
          gte={value.outputDateFrom ?? undefined}
          lte={value.outputDateTo ?? undefined}
          onChange={({ gte, lte }) => {
            patch({ outputDateFrom: gte?.slice(0, 10) ?? null, outputDateTo: lte?.slice(0, 10) ?? null })
          }}
        />
        {MATERIAL_TEXT_FIELDS.slice(2).map((field) => (
          <CompactFormField key={field.key} id={field.key} label={field.label}>
            <FilterTextInput
              id={field.key}
              type="text"
              value={value[field.key]}
              onValueChange={(next) => {
                patch({ [field.key]: next })
              }}
              placeholder={`${field.label}を入力`}
              data-testid={`steel-order-filter-${field.key}`}
              clearButtonTestId={`steel-order-filter-${field.key}-clear`}
            />
          </CompactFormField>
        ))}
        {MATERIAL_TEXT_FIELDS.slice(0, 2).map((field) => (
          <CompactFormField key={field.key} id={field.key} label={field.label}>
            <FilterTextInput
              id={field.key}
              type="text"
              value={value[field.key]}
              onValueChange={(next) => {
                patch({ [field.key]: next })
              }}
              placeholder={`${field.label}を入力`}
              data-testid={`steel-order-filter-${field.key}`}
              clearButtonTestId={`steel-order-filter-${field.key}-clear`}
            />
          </CompactFormField>
        ))}
        <DateRangeField
          idPrefix="importedAt"
          testIdPrefix="steel-order-filter"
          label="発注データ更新日"
          gte={value.importedAtFrom ?? undefined}
          lte={value.importedAtTo ?? undefined}
          onChange={({ gte, lte }) => {
            patch({ importedAtFrom: gte?.slice(0, 10) ?? null, importedAtTo: lte?.slice(0, 10) ?? null })
          }}
        />
      </div>
    </FilterBarShell>
  )
}
